// --- Helper Functions ---

/**
 * Calculates the fixed monthly payment for loans assuming daily compounding interest (e.g., Federal loans).
 * Uses the standard annuity formula but with an effective monthly rate derived from daily compounding.
 * Matches the calculation typically used for Standard, Extended, Graduated base, Refinance.
 * @param principal The initial loan principal.
 * @param annualInterestRate The nominal annual interest rate (as a decimal, e.g., 0.05 for 5%).
 * @param termMonths The loan term in months.
 * @returns The calculated fixed monthly payment. Returns null if term is non-positive or calculation fails.
 */
export function calculateFixedPayment(
  principal: number,
  annualInterestRate: number,
  termMonths: number
): number | null {
  if (principal < 0 || termMonths <= 0 || annualInterestRate < 0) {
    if (principal === 0) return 0;
    console.warn(
      '[Fixed Payment Calc] Invalid input: P=%s, Rate=%s, Months=%s',
      principal,
      annualInterestRate,
      termMonths
    );
    return null;
  }
  if (principal === 0) return 0; // Handle 0 principal

  if (annualInterestRate === 0) {
    return principal / termMonths;
  }

  const dailyRate = annualInterestRate / 365.25;
  const avgDaysPerMonth = 365.25 / 12;
  // Calculate effective monthly rate from daily compounding
  const effectiveMonthlyRate = Math.pow(1 + dailyRate, avgDaysPerMonth) - 1;

  // Handle cases where rate is effectively zero after calculation
  if (effectiveMonthlyRate <= 0 || !isFinite(effectiveMonthlyRate)) {
    console.warn(
      '[Fixed Payment Calc] Effective monthly rate is non-positive or invalid. Using simple division.',
      effectiveMonthlyRate
    );
    return principal / termMonths;
  }

  // Standard annuity formula using the *effective* monthly rate
  const factor = Math.pow(1 + effectiveMonthlyRate, termMonths);
  // Avoid division by zero if factor is 1
  if (factor === 1 || !isFinite(factor)) {
    console.warn('[Fixed Payment Calc] Power factor is invalid. Using simple division.', factor);
    return principal / termMonths;
  }

  const payment = (principal * effectiveMonthlyRate * factor) / (factor - 1);

  return isFinite(payment) ? payment : null; // Return null if payment calculation results in NaN/Infinity
}

/**
 * Calculates the standard monthly payment for a loan assuming standard monthly compounding.
 * Used for calculating payment caps (e.g., 10-year standard cap for PAYE/IBR) and ICR base payment (12-year).
 * @param principal The total loan amount (P).
 * @param annualInterestRate The annual interest rate (as a decimal, e.g., 0.05 for 5%).
 * @param termMonths The loan term in MONTHS (e.g., 120 for 10-year cap).
 * @returns The calculated standard monthly payment, or null if inputs are invalid.
 */
export function calculateStandardPayment(
  principal: number,
  annualInterestRate: number,
  termMonths: number
): number | null {
  if (principal < 0 || termMonths <= 0 || annualInterestRate < 0) {
    if (principal === 0) return 0; // Edge case: 0 balance means 0 payment
    console.warn(
      '[Standard Payment Calc] Invalid input: P=%s, Rate=%s, Months=%s',
      principal,
      annualInterestRate,
      termMonths
    );
    return null;
  }
  if (principal === 0) return 0; // Handle 0 principal

  const monthlyRate = annualInterestRate / 12;
  const numberOfPayments = termMonths;

  // Handle edge case for 0% interest rate
  if (monthlyRate === 0 || !isFinite(monthlyRate)) {
    // Avoid division by zero if numberOfPayments is 0
    return numberOfPayments > 0 ? principal / numberOfPayments : null; // Cannot determine payment if term is 0
  }

  // Standard amortization formula: M = P * [r(1+r)^n] / [(1+r)^n – 1]
  const factor = Math.pow(1 + monthlyRate, numberOfPayments);
  // Avoid division by zero if factor is 1 or invalid
  if (factor === 1 || !isFinite(factor)) {
    console.warn('[Standard Payment Calc] Power factor is invalid. Using simple division.', factor);
    return numberOfPayments > 0 ? principal / numberOfPayments : null;
  }

  const monthlyPayment = (principal * (monthlyRate * factor)) / (factor - 1);

  // Add check for NaN or Infinity
  return isFinite(monthlyPayment) ? monthlyPayment : null;
}

// ICR Factor and related tables have been moved to incomeCalculations.ts
